import { Colors, CollapsibleSection, Container, Heading, Icon, Text } from '@amelio/ui-library';
import { useId, useState } from 'react';

import type { MeetingTemplate } from '@/types/thematic-meetings';
import type { IconProps } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { MEETINGS_TITLE } from '@/pages/ThematicMeetings/components/meetingStyles';
import { getTextByLanguage } from '@/utils/localization';

interface MeetingTipsSectionProps {
    template: MeetingTemplate | undefined;
    dataTestId?: string;
}

// One distinct icon per bullet position rather than a repeated generic bullet; cycles past four
// items, and is shared by every `IconBulletList` below.
const BULLET_ICON_NAMES: ReadonlyArray<IconProps['name']> = [
    'lucide-eye',
    'messages-message-square',
    'design-blend',
    'arrow-login-1',
];

interface TipsBlockProps {
    title: string;
    iconName: IconProps['name'];
    dataTestId?: string;
    children: ReactNode;
}

/**
 * One block of the section: a white card of its own on the section's glass, named by its title so a
 * screen reader can move from block to block.
 */
const TipsBlock = ({ title, iconName, dataTestId, children }: TipsBlockProps) => {
    const titleId = useId();

    return (
        <section
            aria-labelledby={titleId}
            data-testid={dataTestId}
        >
            <Container
                p={'lg'}
                gap={'md'}
                borderRadius={'xl'}
                border={'subtle'}
                background={'translucent-strong'}
            >
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={iconName}
                        size={'sm'}
                        color={Colors.NAVY}
                        ariaHidden
                    />

                    <Heading
                        id={titleId}
                        level={4}
                        weight={'semibold'}
                        color={Colors.BASE_600}
                    >
                        {title}
                    </Heading>
                </div>

                {children}
            </Container>
        </section>
    );
};

/** Bulleted lines, each with its own cycling icon: the template tips and the observations. */
const IconBulletList = ({ items }: { items: string[] }) => (
    // Body text of the plan (`Text`'s own default), like the preparation questions: these lines are
    // read, not skimmed. `mt-0.5` nudges the bullet icon onto the first line's optical centre.
    <ul
        role={'list'}
        className={'flex flex-col gap-3'}
    >
        {items.map((item, index) => (
            <li
                key={index}
                className={'flex items-start gap-2'}
            >
                <Icon
                    name={BULLET_ICON_NAMES[index % BULLET_ICON_NAMES.length]}
                    size={'md'}
                    color={Colors.NAVY}
                    className={'mt-0.5 flex-shrink-0'}
                    ariaHidden
                />
                <Text>{item}</Text>
            </li>
        ))}
    </ul>
);

export const MeetingTipsSection = ({
    template,
    dataTestId = 'thematic-meeting-tips',
}: MeetingTipsSectionProps) => {
    const language = useSelectedAppLanguage();
    const { t } = useAmelioTranslations();
    const [isOpen, setIsOpen] = useState(false);

    if (!template) {
        return null;
    }

    return (
        <CollapsibleSection
            isOpen={isOpen}
            onToggle={() => setIsOpen((current) => !current)}
            variant={'glass'}
            dataTestId={dataTestId}
            header={
                <div className={'flex items-center gap-3'}>
                    <Icon
                        name={'lucide-clipboard-list'}
                        size={'md'}
                        color={Colors.BASE_600}
                        ariaHidden
                    />

                    <Heading
                        level={3}
                        color={Colors.NAVY}
                        className={MEETINGS_TITLE}
                    >
                        {t('thematicMeetings.plan.tipsAccordionTitle')}
                    </Heading>
                </div>
            }
        >
            <div className={'flex flex-col gap-8 px-10 pt-6 pb-8'}>
                {template.tips.length > 0 && (
                    <TipsBlock
                        title={t('thematicMeetings.plan.templateTipsTitle')}
                        iconName={'lucide-lightbulb'}
                        dataTestId={'thematic-meeting-template-tips'}
                    >
                        <IconBulletList
                            items={template.tips.map((tip) => getTextByLanguage(tip, language))}
                        />
                    </TipsBlock>
                )}

                <TipsBlock
                    title={t('thematicMeetings.plan.observationsTitle')}
                    iconName={'search-search-normal-1'}
                >
                    <IconBulletList
                        items={template.observations.map((observation) =>
                            getTextByLanguage(observation, language),
                        )}
                    />
                </TipsBlock>

                <TipsBlock
                    title={t('thematicMeetings.plan.conversationTipsTitle')}
                    iconName={'lucide-lightbulb'}
                    dataTestId={'thematic-meeting-conversation-tips'}
                >
                    <ul
                        role={'list'}
                        className={'grid gap-7 md:grid-cols-3'}
                    >
                        {template.conversationTips.map((tip, index) => (
                            <li
                                key={index}
                                className={'flex'}
                            >
                                {/* Solid white, so the tile reads apart from its translucent block. */}
                                <Container
                                    p={'md'}
                                    gap={'xs'}
                                    borderRadius={'xl'}
                                    background={'white'}
                                    className={'flex-1'}
                                >
                                    <Text
                                        weight={'semibold'}
                                        color={Colors.BASE_600}
                                    >
                                        {getTextByLanguage(tip.title, language)}
                                    </Text>
                                    <Text color={Colors.BASE_500}>
                                        {getTextByLanguage(tip.description, language)}
                                    </Text>
                                </Container>
                            </li>
                        ))}
                    </ul>
                </TipsBlock>
            </div>
        </CollapsibleSection>
    );
};
